import {
  Button,
  type ButtonProps,
  Group,
  NumberInput as MantineNumberInput,
  type NumberInputProps as MantineNumberInputProps,
  type NumberInputHandlers,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { forwardRef, type HTMLAttributes, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

export type EggNumberInputProps = MantineNumberInputProps;

type InputValueChangeButtonProps = ButtonProps &
  HTMLAttributes<HTMLButtonElement> & {
    hideArrowsOnMobile?: boolean;
  };

const InputValueChangeButton = ({
  children,
  ...props
}: InputValueChangeButtonProps) => (
  <Button
    color="dark.7"
    h={22}
    p={0}
    radius={6}
    variant="filled"
    w={30}
    {...props}
  >
    {children}
  </Button>
);

export const EggNumberInput = forwardRef<
  HTMLInputElement,
  EggNumberInputProps & { hideArrowsOnMobile?: boolean }
>(({ rightSection, hideArrowsOnMobile = false, ...props }, ref) => {
  const handlersRef = useRef<NumberInputHandlers>(null);
  const isMobile = useMediaQuery('(max-width: 1024px)');

  return (
    <MantineNumberInput
      decimalScale={2}
      fixedDecimalScale
      flex={1}
      handlersRef={handlersRef}
      ref={ref}
      rightSection={
        <Group gap={4} pr={isMobile && hideArrowsOnMobile ? 12 : undefined}>
          {rightSection}
          <div
            className={twMerge(
              'flex flex-col gap-0.5',
              hideArrowsOnMobile && 'mantine-lg:flex hidden'
            )}
          >
            <InputValueChangeButton
              data-test-id="increment-button"
              onClick={() => handlersRef.current?.increment()}
            >
              <CaretUpIcon size={14} />
            </InputValueChangeButton>
            <InputValueChangeButton
              data-test-id="decrement-button"
              onClick={() => handlersRef.current?.decrement()}
            >
              <CaretDownIcon size={14} />
            </InputValueChangeButton>
          </div>
        </Group>
      }
      rightSectionWidth="auto"
      thousandSeparator=","
      {...props}
      className="!text-right"
      ta="right"
    />
  );
});

EggNumberInput.displayName = 'EggNumberInput';
